import { useMemo, useState } from 'react';
import {
    AlertTriangle,
    Boxes,
    Camera,
    Check,
    ChevronRight,
    FileText,
    HelpCircle,
    Layers,
    Monitor,
    Package,
    Printer,
    RotateCcw,
    ScanLine,
    Smartphone,
    Truck,
    Upload,
    X,
} from './lucideShim';

/* ------------------------------------------------------------------ *
 * PROTOTIPO NAO FUNCIONAL - validacao de processo
 * Todos os dados sao ficticios. Nenhuma chamada de API.
 * O objetivo e responder: a ordem das etapas esta certa?
 * ------------------------------------------------------------------ */

const STEPS = [
    { id: 'analise', label: 'Analise do romaneio', persona: 'Karine', device: 'pc', icon: FileText },
    { id: 'cadastro', label: 'Cadastro logistico', persona: 'Karine', device: 'pc', icon: Layers, skippable: true },
    { id: 'kits', label: 'Preparo de kits', persona: 'Logistica', device: 'pc', icon: Boxes, skippable: true },
    { id: 'picking', label: 'Separacao e etiquetagem', persona: 'Operador', device: 'coletor', icon: ScanLine },
    { id: 'politica', label: 'Volume ou pallet', persona: 'Wagner', device: 'pc', icon: Package, skippable: true },
    { id: 'montagem', label: 'Montagem dos containers', persona: 'Operador', device: 'coletor', icon: Package },
    { id: 'declaracao', label: 'Declaracao no Mercado Livre', persona: 'Karine', device: 'pc', icon: Upload },
    { id: 'depara', label: 'De-para das etiquetas ML', persona: 'Operador', device: 'coletor', icon: ScanLine },
    { id: 'pronto', label: 'Pronto para coleta', persona: 'Logistica', device: 'pc', icon: Camera },
    { id: 'coleta', label: 'Conferencia de coleta', persona: 'Operador', device: 'coletor', icon: Truck },
];

const BASE_ITEMS = [
    {
        invId: 'INV-8842',
        mlb: 'MLB1122334455',
        sku: 'TECL-01',
        name: 'Teclado mecanico K6 - Preto',
        ean: '7891234000011',
        qty: 60,
        labeled: true,
        volumePolicy: 'opcional',
        unitsPerPallet: 240,
        registered: true,
        kit: false,
    },
    {
        invId: 'INV-9107',
        mlb: 'MLB9988776655',
        sku: 'TECL-01',
        name: 'Teclado mecanico K6 - Preto (2o anuncio)',
        ean: '7891234000011',
        qty: 40,
        labeled: true,
        volumePolicy: 'opcional',
        unitsPerPallet: 240,
        registered: true,
        kit: false,
    },
    {
        invId: 'INV-5530',
        mlb: 'MLB4455667788',
        sku: 'RAC-20',
        name: 'Racao premium adulto 20kg',
        ean: '7899876000022',
        qty: 70,
        labeled: false,
        volumePolicy: 'nunca',
        unitsPerPallet: 70,
        registered: true,
        kit: false,
    },
    {
        invId: 'INV-7781',
        mlb: 'MLB2233445566',
        sku: 'FIG-COPA',
        name: 'Pacote figurinhas Copa - kit 10un',
        ean: '7890001000033',
        qty: 500,
        labeled: true,
        volumePolicy: 'obrigatorio',
        unitsPerPallet: 1200,
        registered: true,
        kit: true,
        kitReadyStock: 320,
    },
    {
        invId: 'INV-6620',
        mlb: 'MLB3344556677',
        sku: 'IMP-L42',
        name: 'Impressora L42 Pro',
        ean: '7893456000044',
        qty: 12,
        labeled: true,
        volumePolicy: 'opcional',
        unitsPerPallet: 24,
        registered: true,
        kit: false,
    },
    {
        invId: 'INV-4409',
        mlb: 'MLB5566778899',
        sku: 'ALB-COPA',
        name: 'Album Copa capa dura',
        ean: '7890001000055',
        qty: 80,
        labeled: null,
        volumePolicy: null,
        unitsPerPallet: null,
        registered: false,
        kit: false,
    },
];

const QUESTIONS = {
    cadastro: 'Item sem cadastro bloqueia a carga inteira (como hoje) ou abre a solicitacao com pendencia?',
    kits: 'A trava de "kit montado" entra na primeira versao? O estoque de kits vira um deposito separado no Bling?',
    picking: 'Bipagem dupla (item antes de etiquetar + etiqueta depois de colar) e obrigatoria ou vira opcao por item?',
    politica: 'A decisao do Wagner deveria vir ANTES do picking, para o operador ja separar por container?',
    montagem: 'A etiqueta interna nasce na criacao do container ou no fechamento? Volume pode sair sem pallet?',
    declaracao: 'As etiquetas do ML realmente distinguem pallet misto de pallet unico? Toda a validacao depende disso.',
    depara: 'Dois Inventory IDs podem dividir o mesmo pallet do ML?',
    pronto: 'As 4 fotos ficam no fim da montagem ou no fim da coleta?',
    coleta: 'A coleta bipa container a container ou basta conferir o total?',
};

/* ---------------------------- primitivos ---------------------------- */

const cls = (...parts) => parts.filter(Boolean).join(' ');

function Btn({ children, variant = 'primary', size = 'md', className, ...rest }) {
    const variants = {
        primary: 'bg-black text-white hover:bg-neutral-800 disabled:bg-neutral-300',
        secondary: 'bg-white text-black border border-neutral-300 hover:border-black disabled:text-neutral-400',
        ghost: 'text-neutral-600 hover:text-black hover:bg-neutral-100',
        danger: 'bg-red-600 text-white hover:bg-red-700',
    };
    const sizes = { sm: 'px-2.5 py-1 text-xs', md: 'px-4 py-2 text-sm', lg: 'px-5 py-2.5 text-base' };
    return (
        <button
            type="button"
            className={cls(
                'inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2',
                'disabled:cursor-not-allowed',
                variants[variant],
                sizes[size],
                className,
            )}
            {...rest}
        >
            {children}
        </button>
    );
}

function Card({ title, subtitle, right, children, className }) {
    return (
        <div className={cls('rounded-xl border border-neutral-200 bg-white p-5', className)}>
            {(title || right) && (
                <div className="mb-4 flex items-start justify-between gap-4">
                    <div>
                        {title && <h3 className="text-base font-bold text-black">{title}</h3>}
                        {subtitle && <p className="mt-0.5 text-sm text-neutral-600">{subtitle}</p>}
                    </div>
                    {right}
                </div>
            )}
            {children}
        </div>
    );
}

function Chip({ children, tone = 'neutral', className }) {
    const tones = {
        neutral: 'bg-neutral-100 text-neutral-700',
        black: 'bg-black text-white',
        green: 'bg-green-100 text-green-800',
        red: 'bg-red-100 text-red-800',
        amber: 'bg-amber-100 text-amber-900',
        indigo: 'bg-indigo-100 text-indigo-800',
    };
    return (
        <span
            className={cls(
                'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                tones[tone],
                className,
            )}
        >
            {children}
        </span>
    );
}

function SimChip() {
    return (
        <span className="rounded border border-dashed border-neutral-400 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-neutral-500">
            simulacao
        </span>
    );
}

function Field({ label, hint, children }) {
    return (
        <label className="block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-neutral-500">{label}</span>
            {children}
            {hint && <span className="mt-1 block text-xs text-neutral-500">{hint}</span>}
        </label>
    );
}

const inputCls =
    'w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-black placeholder:text-neutral-400 focus:border-black focus:outline-none';

function Progress({ done, total }) {
    const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
    const complete = done >= total && total > 0;
    return (
        <div className="flex items-center gap-2">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-neutral-200">
                <div
                    className={cls('h-full rounded-full transition-all', complete ? 'bg-green-600' : 'bg-black')}
                    style={{ width: `${pct}%` }}
                />
            </div>
            <span className={cls('shrink-0 font-mono text-xs', complete ? 'text-green-700' : 'text-neutral-600')}>
                {done}/{total}
            </span>
        </div>
    );
}

function Banner({ tone = 'neutral', icon: Icon, title, children }) {
    const tones = {
        neutral: 'border-neutral-200 bg-neutral-50 text-neutral-800',
        green: 'border-green-200 bg-green-50 text-green-900',
        red: 'border-red-200 bg-red-50 text-red-900',
        amber: 'border-amber-200 bg-amber-50 text-amber-900',
    };
    return (
        <div className={cls('flex items-start gap-3 rounded-xl border p-4', tones[tone])}>
            {Icon && <Icon size={20} className="mt-0.5 shrink-0" />}
            <div className="text-sm">
                {title && <p className="font-bold">{title}</p>}
                {children}
            </div>
        </div>
    );
}

/** Campo de bipagem. Estado local para nao perder foco a cada render. */
function ScanBox({ onScan, placeholder = 'Bipe ou digite o codigo', disabled, error, autoFocus }) {
    const [value, setValue] = useState('');
    const submit = () => {
        const code = value.trim();
        if (!code) return;
        onScan(code);
        setValue('');
    };
    return (
        <div className="space-y-2">
            <div className="flex gap-2">
                <div className="relative flex-1">
                    <ScanLine size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <input
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus={autoFocus}
                        className={cls(inputCls, 'py-3 pl-10 font-mono text-base', error && 'border-red-500')}
                        placeholder={placeholder}
                        value={value}
                        disabled={disabled}
                        onChange={(e) => setValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                submit();
                            }
                        }}
                    />
                </div>
                <Btn size="lg" onClick={submit} disabled={disabled}>
                    Bipar
                </Btn>
            </div>
            {error && (
                <p className="flex items-center gap-1.5 text-sm font-semibold text-red-700">
                    <X size={14} /> {error}
                </p>
            )}
        </div>
    );
}

function Th({ children, align = 'left' }) {
    return (
        <th
            className={cls(
                'border-b border-neutral-200 px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-neutral-500',
                align === 'right' && 'text-right',
                align === 'center' && 'text-center',
                align === 'left' && 'text-left',
            )}
        >
            {children}
        </th>
    );
}

function Td({ children, align = 'left', className }) {
    return (
        <td
            className={cls(
                'border-b border-neutral-100 px-3 py-2 text-sm text-neutral-700',
                align === 'right' && 'text-right',
                align === 'center' && 'text-center',
                className,
            )}
        >
            {children}
        </td>
    );
}

/* ------------------------------ app ------------------------------ */

export default function OutboundFlowPrototype() {
    const [stepIndex, setStepIndex] = useState(0);
    const [coletor, setColetor] = useState(false);
    const [showQuestions, setShowQuestions] = useState(true);

    // etapa 0
    const [romaneioLoaded, setRomaneioLoaded] = useState(false);
    const [analysed, setAnalysed] = useState(false);
    const [outboundOpen, setOutboundOpen] = useState(false);

    // etapa 1
    const [items, setItems] = useState(BASE_ITEMS);
    const [reg, setReg] = useState({ labeled: 'sim', volumePolicy: 'opcional', unitsPerPallet: '300' });

    // etapa 2
    const [kitsReleased, setKitsReleased] = useState(false);

    // etapa 3
    const [printed, setPrinted] = useState({});
    const [picked, setPicked] = useState({});
    const [pickError, setPickError] = useState('');
    const [qtyDraft, setQtyDraft] = useState({});

    // etapa 4
    const [policy, setPolicy] = useState({});

    // etapa 5
    const [containers, setContainers] = useState([]);
    const [activeContainer, setActiveContainer] = useState(null);
    const [buildError, setBuildError] = useState('');
    const [palletQty, setPalletQty] = useState({});

    // etapa 6
    const [declared, setDeclared] = useState(false);
    const [mlLabels, setMlLabels] = useState([]);

    // etapa 7
    const [deparaError, setDeparaError] = useState('');

    // etapa 8
    const [photos, setPhotos] = useState(0);
    const [transport, setTransport] = useState({ haulier: '', driver: '', plate: '' });

    // etapa 9
    const [loaded, setLoaded] = useState([]);
    const [video, setVideo] = useState(false);
    const [collectError, setCollectError] = useState('');

    const step = STEPS[stepIndex];
    const activeItems = items;
    const pendingReg = activeItems.filter((i) => !i.registered);
    const kitItems = activeItems.filter((i) => i.kit);
    const kitShort = kitItems.filter((i) => i.qty > (i.kitReadyStock || 0));

    const estimatedPallets = useMemo(
        () =>
            activeItems
                .filter((i) => i.registered && i.unitsPerPallet)
                .reduce((acc, i) => acc + i.qty / i.unitsPerPallet, 0),
        [activeItems],
    );

    const pickDone = activeItems.every((i) => (picked[i.invId] || 0) >= i.qty);
    const printsDone = activeItems.filter((i) => i.labeled).every((i) => printed[i.invId]);

    const effectivePolicy = (item) => {
        if (item.volumePolicy === 'nunca') return 'pallet';
        if (item.volumePolicy === 'obrigatorio') return 'volume';
        return policy[item.invId] || null;
    };
    const optionalItems = activeItems.filter((i) => i.volumePolicy === 'opcional');
    const policyDone = optionalItems.every((i) => policy[i.invId]);

    const containerItemTotal = (c) => Object.values(c.items).reduce((a, b) => a + b, 0);
    const containerInvIds = (c) => {
        const ids = new Set(Object.keys(c.items));
        (c.children || []).forEach((code) => {
            const child = containers.find((x) => x.code === code);
            if (child) Object.keys(child.items).forEach((id) => ids.add(id));
        });
        return [...ids];
    };
    const isMixed = (c) => containerInvIds(c).length > 1;

    const packedQty = useMemo(() => {
        const acc = {};
        containers.forEach((c) => {
            Object.entries(c.items).forEach(([invId, q]) => {
                acc[invId] = (acc[invId] || 0) + q;
            });
        });
        return acc;
    }, [containers]);

    const packDone =
        activeItems.every((i) => (packedQty[i.invId] || 0) >= i.qty) &&
        containers.length > 0 &&
        containers.every((c) => !c.open);

    const shippable = containers.filter((c) => !c.parent);
    const pallets = shippable.filter((c) => c.type === 'pallet');
    const looseVolumes = shippable.filter((c) => c.type === 'volume');
    const deparaDone = shippable.length > 0 && shippable.every((c) => c.mlLabel);

    const stepDone = {
        analise: outboundOpen,
        cadastro: pendingReg.length === 0,
        kits: kitShort.length === 0 || kitsReleased,
        picking: pickDone && printsDone,
        politica: policyDone,
        montagem: packDone,
        declaracao: declared && mlLabels.length > 0,
        depara: deparaDone,
        pronto: photos >= 4 && transport.haulier && transport.driver && transport.plate,
        coleta: loaded.length >= shippable.length && shippable.length > 0 && video,
    };

    const skipped = {
        cadastro: pendingReg.length === 0 && !outboundOpen,
        kits: kitShort.length === 0,
        politica: optionalItems.length === 0,
    };

    const resetAll = () => {
        setStepIndex(0);
        setRomaneioLoaded(false);
        setAnalysed(false);
        setOutboundOpen(false);
        setItems(BASE_ITEMS);
        setKitsReleased(false);
        setPrinted({});
        setPicked({});
        setQtyDraft({});
        setPolicy({});
        setContainers([]);
        setActiveContainer(null);
        setPalletQty({});
        setDeclared(false);
        setMlLabels([]);
        setPhotos(0);
        setTransport({ haulier: '', driver: '', plate: '' });
        setLoaded([]);
        setVideo(false);
        setPickError('');
        setBuildError('');
        setDeparaError('');
        setCollectError('');
    };

    /* --------------------------- acoes --------------------------- */

    const saveRegistration = () => {
        setItems((prev) =>
            prev.map((i) =>
                i.registered
                    ? i
                    : {
                          ...i,
                          registered: true,
                          labeled: reg.labeled === 'sim',
                          volumePolicy: reg.volumePolicy,
                          unitsPerPallet: Number(reg.unitsPerPallet) || 300,
                      },
            ),
        );
    };

    const handlePickScan = (code) => {
        setPickError('');
        const target = activeItems.find(
            (i) => i.invId.toLowerCase() === code.toLowerCase() || i.ean === code,
        );
        if (!target) {
            setPickError(`Codigo ${code} nao pertence a esta carga.`);
            return;
        }
        if (target.labeled && !printed[target.invId]) {
            setPickError(`Imprima e cole as etiquetas de ${target.invId} antes de conferir.`);
            return;
        }
        const current = picked[target.invId] || 0;
        if (current >= target.qty) {
            setPickError(`${target.name} ja esta com a quantidade completa.`);
            return;
        }
        setPicked({ ...picked, [target.invId]: current + 1 });
    };

    const bumpPick = (item, amount) => {
        const current = picked[item.invId] || 0;
        setPicked({ ...picked, [item.invId]: Math.min(item.qty, current + amount) });
    };

    const createContainer = (type) => {
        const seq = containers.filter((c) => c.type === type).length + 1;
        const code = `${type === 'pallet' ? 'PLT' : 'VOL'}-${String(seq).padStart(4, '0')}`;
        const next = { code, type, open: true, items: {}, children: [], mlLabel: null, parent: null, confirmed: false };
        setContainers([...containers, next]);
        setActiveContainer(code);
        setBuildError('');
    };

    const updateContainer = (code, patch) =>
        setContainers((prev) => prev.map((c) => (c.code === code ? { ...c, ...patch } : c)));

    const addToContainer = (code, invId, qty) => {
        setBuildError('');
        const container = containers.find((c) => c.code === code);
        const item = activeItems.find((i) => i.invId === invId);
        if (!container || !item) return;

        const already = packedQty[invId] || 0;
        if (already + qty > item.qty) {
            setBuildError(`Sobra apenas ${item.qty - already} un. de ${item.sku} para alocar.`);
            return;
        }
        if (container.type === 'pallet' && item.unitsPerPallet) {
            const inThis = container.items[invId] || 0;
            if (inThis + qty > item.unitsPerPallet) {
                setBuildError(
                    `Limite do pallet: ${item.unitsPerPallet} un. de ${item.sku} por pallet padrao Mercado Livre.`,
                );
                return;
            }
        }
        updateContainer(code, { items: { ...container.items, [invId]: (container.items[invId] || 0) + qty } });
    };

    const handleBuildScan = (code) => {
        setBuildError('');
        const container = containers.find((c) => c.code === activeContainer);
        if (!container) {
            setBuildError('Abra um container antes de bipar.');
            return;
        }
        if (!container.confirmed) {
            if (code.toLowerCase() === container.code.toLowerCase()) {
                updateContainer(container.code, { confirmed: true });
                return;
            }
            setBuildError(`Bipe primeiro a etiqueta interna ${container.code} que voce acabou de colar.`);
            return;
        }
        const closedVolume = containers.find(
            (c) => c.code.toLowerCase() === code.toLowerCase() && c.type === 'volume' && !c.open && !c.parent,
        );
        if (closedVolume && container.type === 'pallet') {
            updateContainer(closedVolume.code, { parent: container.code });
            updateContainer(container.code, { children: [...(container.children || []), closedVolume.code] });
            return;
        }
        const item = activeItems.find((i) => i.invId.toLowerCase() === code.toLowerCase() || i.ean === code);
        if (!item) {
            setBuildError(`Codigo ${code} nao reconhecido.`);
            return;
        }
        const want = effectivePolicy(item);
        if (want === 'volume' && container.type === 'pallet') {
            setBuildError(`${item.sku} foi marcado para ir em volume. Monte o volume antes de subir no pallet.`);
            return;
        }
        if (want === 'pallet' && container.type === 'volume') {
            setBuildError(`${item.sku} foi marcado para ir direto no pallet.`);
            return;
        }
        addToContainer(container.code, item.invId, 1);
    };

    const generateMlLabels = () => {
        const labels = [];
        pallets.forEach((p, idx) => {
            labels.push({
                code: `ML-PLT-${77300 + idx}`,
                type: 'pallet',
                mixed: isMixed(p),
                used: false,
            });
        });
        looseVolumes.forEach((v, idx) => {
            labels.push({ code: `ML-VOL-${44100 + idx}`, type: 'volume', mixed: isMixed(v), used: false });
        });
        setMlLabels(labels);
    };

    const attachMlLabel = (containerCode, labelCode) => {
        setDeparaError('');
        const container = shippable.find((c) => c.code === containerCode);
        const label = mlLabels.find((l) => l.code === labelCode);
        if (!container || !label) return;
        if (label.used) {
            setDeparaError(`${label.code} ja foi vinculada a outro container.`);
            return;
        }
        if (label.type !== container.type) {
            setDeparaError(
                `${label.code} e etiqueta de ${label.type}. ${container.code} e um ${container.type}.`,
            );
            return;
        }
        if (label.mixed !== isMixed(container)) {
            setDeparaError(
                label.mixed
                    ? `${label.code} e de ${label.type} MISTO e ${container.code} tem um unico Inventory ID. Bloqueado.`
                    : `${label.code} e de ${label.type} UNICO e ${container.code} tem mais de um Inventory ID. Bloqueado.`,
            );
            return;
        }
        setMlLabels((prev) => prev.map((l) => (l.code === labelCode ? { ...l, used: true } : l)));
        updateContainer(containerCode, { mlLabel: labelCode });
    };

    const handleCollectScan = (code) => {
        setCollectError('');
        const container = shippable.find(
            (c) =>
                c.code.toLowerCase() === code.toLowerCase() ||
                String(c.mlLabel || '').toLowerCase() === code.toLowerCase(),
        );
        if (!container) {
            setCollectError(`${code} nao e um container desta carga.`);
            return;
        }
        if (loaded.some((l) => l.code === container.code)) {
            setCollectError(`${container.code} ja foi embarcado.`);
            return;
        }
        setLoaded([
            ...loaded,
            {
                code: container.code,
                ml: container.mlLabel,
                at: new Date().toLocaleTimeString('pt-BR'),
            },
        ]);
    };

    /* --------------------------- render --------------------------- */

    const Question = ({ id }) =>
        showQuestions && QUESTIONS[id] ? (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <HelpCircle size={16} className="mt-0.5 shrink-0 text-amber-700" />
                <p className="text-sm text-amber-900">
                    <span className="font-bold">Em aberto: </span>
                    {QUESTIONS[id]}
                </p>
            </div>
        ) : null;

    const renderStep = () => {
        switch (step.id) {
            /* ---------- 1. analise ---------- */
            case 'analise':
                if (!analysed) {
                    return (
                        <div className="mx-auto max-w-xl space-y-4">
                            <Card title="Nova solicitacao de envio" subtitle="Romaneio do Mercado Livre em PDF">
                                <button
                                    type="button"
                                    onClick={() => setRomaneioLoaded(true)}
                                    className="w-full rounded-xl border-2 border-dashed border-neutral-300 p-8 text-center transition-colors hover:border-black"
                                >
                                    <Upload size={40} className="mx-auto text-neutral-400" />
                                    {romaneioLoaded ? (
                                        <p className="mt-2 font-semibold text-black">romaneio_full_2026-07-28.pdf</p>
                                    ) : (
                                        <p className="mt-2 text-sm text-neutral-500">
                                            Clique para carregar o romaneio de exemplo
                                        </p>
                                    )}
                                </button>
                                <div className="mt-4 flex items-center gap-3">
                                    <Btn size="lg" disabled={!romaneioLoaded} onClick={() => setAnalysed(true)}>
                                        Analisar carga
                                    </Btn>
                                    <SimChip />
                                </div>
                            </Card>
                        </div>
                    );
                }
                return (
                    <div className="space-y-4">
                        <Banner
                            tone={pendingReg.length ? 'red' : 'green'}
                            icon={pendingReg.length ? AlertTriangle : Check}
                            title={
                                pendingReg.length
                                    ? `${pendingReg.length} item sem cadastro logistico`
                                    : 'Carga aprovada na pre-conferencia'
                            }
                        >
                            <p className="mt-1">
                                Pallets estimados: <strong className="font-mono">{estimatedPallets.toFixed(2)}</strong>{' '}
                                <span className="text-xs">
                                    (estimativa por unidades / pallet — o numero real sai da montagem)
                                </span>
                            </p>
                        </Banner>
                        <Card>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr>
                                            <Th>Inventory ID</Th>
                                            <Th>SKU</Th>
                                            <Th>Descricao</Th>
                                            <Th>EAN</Th>
                                            <Th align="center">Un.</Th>
                                            <Th align="center">Un./pallet ML</Th>
                                            <Th>Validacao</Th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activeItems.map((i) => (
                                            <tr key={i.invId} className={cls(!i.registered && 'bg-red-50')}>
                                                <Td className="font-mono font-bold text-black">{i.invId}</Td>
                                                <Td className="font-mono">{i.sku}</Td>
                                                <Td>{i.name}</Td>
                                                <Td className="font-mono text-xs">{i.ean}</Td>
                                                <Td align="center" className="font-semibold text-black">
                                                    {i.qty}
                                                </Td>
                                                <Td align="center">{i.unitsPerPallet || '-'}</Td>
                                                <Td>
                                                    {i.registered ? (
                                                        <span className="text-xs text-neutral-500">OK</span>
                                                    ) : (
                                                        <span className="text-xs font-bold text-red-700">
                                                            Sem cadastro logistico
                                                        </span>
                                                    )}
                                                </Td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="mt-4 rounded-lg bg-neutral-50 p-3 text-sm text-neutral-700">
                                <strong>Repare nas duas primeiras linhas.</strong> Mesmo SKU (TECL-01), mesmo EAN, dois
                                Inventory IDs. Sao duas linhas separadas, duas etiquetas diferentes e duas conferencias.
                                A carga e contada por Inventory ID, nunca por SKU.
                            </div>
                            <div className="mt-4">
                                <Btn size="lg" onClick={() => setOutboundOpen(true)} disabled={outboundOpen}>
                                    {outboundOpen ? 'Solicitacao aberta' : 'Abrir solicitacao'}
                                </Btn>
                            </div>
                        </Card>
                    </div>
                );

            /* ---------- 2. cadastro ---------- */
            case 'cadastro':
                if (pendingReg.length === 0) {
                    return (
                        <div className="space-y-4">
                            <Banner tone="green" icon={Check} title="Nenhum cadastro pendente">
                                <p>Todos os Inventory IDs desta carga ja tem cadastro logistico. Etapa concluida.</p>
                            </Banner>
                            <Question id="cadastro" />
                        </div>
                    );
                }
                return (
                    <div className="space-y-4">
                        <Banner tone="amber" icon={AlertTriangle} title="Cadastro logistico pendente">
                            <p>A carga nao avanca enquanto estes Inventory IDs nao tiverem cadastro.</p>
                        </Banner>
                        {pendingReg.map((i) => (
                            <Card key={i.invId} title={i.name} subtitle={`${i.invId} · ${i.mlb} · SKU ${i.sku}`}>
                                <div className="grid gap-4 sm:grid-cols-3">
                                    <Field label="Etiqueta de item obrigatoria">
                                        <select
                                            className={inputCls}
                                            value={reg.labeled}
                                            onChange={(e) => setReg({ ...reg, labeled: e.target.value })}
                                        >
                                            <option value="sim">Sim, etiquetar unidade por unidade</option>
                                            <option value="nao">Nao, vai pelo EAN</option>
                                        </select>
                                    </Field>
                                    <Field label="Politica de volume">
                                        <select
                                            className={inputCls}
                                            value={reg.volumePolicy}
                                            onChange={(e) => setReg({ ...reg, volumePolicy: e.target.value })}
                                        >
                                            <option value="obrigatorio">Sempre em volume</option>
                                            <option value="opcional">Opcional, o Wagner decide</option>
                                            <option value="nunca">Nunca, vai direto no pallet</option>
                                        </select>
                                    </Field>
                                    <Field label="Unidades por pallet padrao ML">
                                        <input
                                            className={inputCls}
                                            value={reg.unitsPerPallet}
                                            onChange={(e) => setReg({ ...reg, unitsPerPallet: e.target.value })}
                                        />
                                    </Field>
                                </div>
                                <div className="mt-4">
                                    <Btn onClick={saveRegistration}>Salvar cadastro</Btn>
                                </div>
                            </Card>
                        ))}
                        <Question id="cadastro" />
                    </div>
                );

            /* ---------- 3. kits ---------- */
            case 'kits':
                if (kitShort.length === 0) {
                    return (
                        <Banner tone="green" icon={Check} title="Sem kit pendente">
                            <p>Nenhum item de kit desta carga precisa de montagem. Etapa pulada.</p>
                        </Banner>
                    );
                }
                return (
                    <div className="space-y-4">
                        <Banner tone="amber" icon={AlertTriangle} title="Kit sem estoque montado">
                            <p>
                                O Bling mostra a possibilidade de montar, nao o que esta montado. Alguem precisa confirmar
                                fisicamente.
                            </p>
                        </Banner>
                        {kitShort.map((i) => (
                            <Card key={i.invId} title={i.name} subtitle={`${i.invId} · SKU ${i.sku}`}>
                                <div className="grid gap-3 sm:grid-cols-3">
                                    <div className="rounded-lg bg-neutral-50 p-3">
                                        <p className="text-xs uppercase text-neutral-500">Necessario</p>
                                        <p className="font-mono text-2xl font-bold text-black">{i.qty}</p>
                                    </div>
                                    <div className="rounded-lg bg-neutral-50 p-3">
                                        <p className="text-xs uppercase text-neutral-500">Kits montados</p>
                                        <p className="font-mono text-2xl font-bold text-black">{i.kitReadyStock}</p>
                                    </div>
                                    <div className="rounded-lg bg-red-50 p-3">
                                        <p className="text-xs uppercase text-red-700">Faltam montar</p>
                                        <p className="font-mono text-2xl font-bold text-red-700">
                                            {i.qty - i.kitReadyStock}
                                        </p>
                                    </div>
                                </div>
                                <p className="mt-3 text-sm text-neutral-600">
                                    Item de kit sempre tem etiquetagem obrigatoria, e o Mercado Livre nao aceita o kit
                                    dividido em dois volumes.
                                </p>
                                <div className="mt-4 flex items-center gap-3">
                                    <Btn onClick={() => setKitsReleased(true)} disabled={kitsReleased}>
                                        {kitsReleased ? 'Liberado' : 'Kits montados, liberar carga'}
                                    </Btn>
                                    <SimChip />
                                </div>
                            </Card>
                        ))}
                        <Question id="kits" />
                    </div>
                );

            /* ---------- 4. picking ---------- */
            case 'picking':
                return (
                    <div className="space-y-4">
                        <Banner tone="neutral" icon={ScanLine} title="Regra: colou, voce bipa">
                            <p>
                                Imprima por Inventory ID, cole nas unidades, depois bipe cada etiqueta. Item sem etiqueta
                                obrigatoria: bipe o EAN uma vez e digite a quantidade.
                            </p>
                        </Banner>
                        <Card title="Conferencia de separacao" subtitle="Confere contra o total do envio, nao por pallet">
                            <ScanBox onScan={handlePickScan} error={pickError} autoFocus />
                            <div className="mt-5 space-y-3">
                                {activeItems.map((i) => {
                                    const done = picked[i.invId] || 0;
                                    return (
                                        <div key={i.invId} className="rounded-lg border border-neutral-200 p-3">
                                            <div className="flex flex-wrap items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <p className="font-semibold text-black">{i.name}</p>
                                                    <p className="font-mono text-xs text-neutral-500">
                                                        {i.sku} · {i.ean} · {i.invId}
                                                    </p>
                                                </div>
                                                {i.labeled ? (
                                                    <Chip tone="black">etiqueta obrigatoria</Chip>
                                                ) : (
                                                    <Chip tone="neutral">sem etiqueta · bipa EAN</Chip>
                                                )}
                                            </div>
                                            <div className="mt-3">
                                                <Progress done={done} total={i.qty} />
                                            </div>
                                            <div className="mt-3 flex flex-wrap items-center gap-2">
                                                {i.labeled &&
                                                    (printed[i.invId] ? (
                                                        <Chip tone="green">
                                                            <Check size={11} /> {i.qty} etiquetas impressas
                                                        </Chip>
                                                    ) : (
                                                        <Btn
                                                            size="sm"
                                                            variant="secondary"
                                                            onClick={() => setPrinted({ ...printed, [i.invId]: true })}
                                                        >
                                                            <Printer size={13} /> Imprimir {i.qty} etiquetas
                                                        </Btn>
                                                    ))}
                                                {!i.labeled && (
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            className={cls(inputCls, 'w-24 py-1 font-mono text-sm')}
                                                            placeholder="qtd"
                                                            value={qtyDraft[i.invId] || ''}
                                                            onChange={(e) =>
                                                                setQtyDraft({ ...qtyDraft, [i.invId]: e.target.value })
                                                            }
                                                        />
                                                        <Btn
                                                            size="sm"
                                                            variant="secondary"
                                                            onClick={() => {
                                                                const n = Number(qtyDraft[i.invId]);
                                                                if (n > 0) bumpPick(i, n);
                                                                setQtyDraft({ ...qtyDraft, [i.invId]: '' });
                                                            }}
                                                        >
                                                            Lancar quantidade
                                                        </Btn>
                                                    </div>
                                                )}
                                                {i.labeled && printed[i.invId] && done < i.qty && (
                                                    <>
                                                        <Btn size="sm" variant="ghost" onClick={() => bumpPick(i, 10)}>
                                                            +10 bipagens
                                                        </Btn>
                                                        <Btn size="sm" variant="ghost" onClick={() => bumpPick(i, i.qty)}>
                                                            completar
                                                        </Btn>
                                                        <SimChip />
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </Card>
                        <Question id="picking" />
                    </div>
                );

            /* ---------- 5. politica ---------- */
            case 'politica':
                return (
                    <div className="space-y-4">
                        <Banner tone="neutral" icon={Package} title="Wagner decide a estrategia, nao a quantidade">
                            <p>
                                Ele diz quais itens vao dentro de uma caixa. Quantos pallets e volumes existem no fim sai
                                da montagem, nao daqui.
                            </p>
                        </Banner>
                        <Card>
                            <div className="space-y-3">
                                {activeItems.map((i) => {
                                    const locked = i.volumePolicy !== 'opcional';
                                    const value = effectivePolicy(i);
                                    return (
                                        <div
                                            key={i.invId}
                                            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-neutral-200 p-3"
                                        >
                                            <div className="min-w-0">
                                                <p className="font-semibold text-black">{i.name}</p>
                                                <p className="font-mono text-xs text-neutral-500">
                                                    {i.sku} · {i.qty} un.
                                                </p>
                                            </div>
                                            {locked ? (
                                                <Chip tone={i.volumePolicy === 'nunca' ? 'neutral' : 'indigo'}>
                                                    {i.volumePolicy === 'nunca'
                                                        ? 'cadastro: direto no pallet'
                                                        : 'cadastro: sempre em volume'}
                                                </Chip>
                                            ) : (
                                                <div className="flex gap-2">
                                                    {['volume', 'pallet'].map((opt) => (
                                                        <Btn
                                                            key={opt}
                                                            size="sm"
                                                            variant={value === opt ? 'primary' : 'secondary'}
                                                            onClick={() => setPolicy({ ...policy, [i.invId]: opt })}
                                                        >
                                                            {opt === 'volume' ? 'Em volume' : 'Direto no pallet'}
                                                        </Btn>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </Card>
                        <Question id="politica" />
                    </div>
                );

            /* ---------- 6. montagem ---------- */
            case 'montagem': {
                const current = containers.find((c) => c.code === activeContainer);
                return (
                    <div className="space-y-4">
                        <Card title="Containers da carga" subtitle="Etiqueta interna do WMS, ainda sem relacao com o ML">
                            <div className="flex flex-wrap gap-2">
                                <Btn onClick={() => createContainer('volume')}>
                                    <Package size={15} /> Novo volume
                                </Btn>
                                <Btn onClick={() => createContainer('pallet')} variant="secondary">
                                    <Layers size={15} /> Novo pallet
                                </Btn>
                            </div>
                            {containers.length > 0 && (
                                <div className="mt-4 space-y-2">
                                    {containers.map((c) => (
                                        <button
                                            key={c.code}
                                            type="button"
                                            onClick={() => setActiveContainer(c.code)}
                                            className={cls(
                                                'flex w-full flex-wrap items-center gap-2 rounded-lg border p-3 text-left transition-colors',
                                                c.code === activeContainer
                                                    ? 'border-black bg-neutral-50'
                                                    : 'border-neutral-200 hover:border-neutral-400',
                                            )}
                                        >
                                            <span className="font-mono font-bold text-black">{c.code}</span>
                                            <Chip tone={c.type === 'pallet' ? 'indigo' : 'neutral'}>{c.type}</Chip>
                                            {c.open ? (
                                                <Chip tone="amber">aberto</Chip>
                                            ) : (
                                                <Chip tone="green">fechado</Chip>
                                            )}
                                            {!c.open && <Chip tone={isMixed(c) ? 'amber' : 'neutral'}>{isMixed(c) ? 'misto' : 'unico'}</Chip>}
                                            {c.parent && <Chip tone="neutral">dentro de {c.parent}</Chip>}
                                            <span className="ml-auto font-mono text-xs text-neutral-500">
                                                {containerItemTotal(c)} un.
                                                {c.children?.length ? ` · ${c.children.length} vol.` : ''}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </Card>

                        {current && current.open && (
                            <Card
                                title={`Montando ${current.code}`}
                                subtitle={
                                    current.confirmed
                                        ? current.type === 'pallet'
                                            ? 'Bipe itens ou etiquetas de volumes fechados'
                                            : 'Bipe cada item que entra na caixa'
                                        : `Cole a etiqueta ${current.code} e bipe ela para confirmar`
                                }
                                right={<Chip tone={current.confirmed ? 'green' : 'amber'}>{current.confirmed ? 'confirmado' : 'aguardando etiqueta'}</Chip>}
                            >
                                <ScanBox
                                    onScan={handleBuildScan}
                                    error={buildError}
                                    placeholder={current.confirmed ? 'Inventory ID, EAN ou codigo de volume' : current.code}
                                    autoFocus
                                />

                                {current.confirmed && (
                                    <div className="mt-4 space-y-2">
                                        {activeItems
                                            .filter((i) => effectivePolicy(i) === (current.type === 'pallet' ? 'pallet' : 'volume'))
                                            .map((i) => {
                                                const inThis = current.items[i.invId] || 0;
                                                const total = packedQty[i.invId] || 0;
                                                return (
                                                    <div
                                                        key={i.invId}
                                                        className="flex flex-wrap items-center gap-3 rounded-lg bg-neutral-50 p-3"
                                                    >
                                                        <div className="min-w-0 flex-1">
                                                            <p className="text-sm font-semibold text-black">{i.sku}</p>
                                                            <p className="text-xs text-neutral-500">
                                                                neste container: {inThis} · alocado no envio: {total}/{i.qty}
                                                            </p>
                                                        </div>
                                                        <input
                                                            className={cls(inputCls, 'w-20 py-1 font-mono text-sm')}
                                                            placeholder="qtd"
                                                            value={palletQty[i.invId] || ''}
                                                            onChange={(e) =>
                                                                setPalletQty({ ...palletQty, [i.invId]: e.target.value })
                                                            }
                                                        />
                                                        <Btn
                                                            size="sm"
                                                            variant="secondary"
                                                            onClick={() => {
                                                                const n = Number(palletQty[i.invId]);
                                                                if (n > 0) addToContainer(current.code, i.invId, n);
                                                                setPalletQty({ ...palletQty, [i.invId]: '' });
                                                            }}
                                                        >
                                                            Lancar
                                                        </Btn>
                                                    </div>
                                                );
                                            })}
                                        {current.type === 'pallet' &&
                                            looseVolumes.filter((v) => !v.open && v.code !== current.code).length > 0 && (
                                                <div className="rounded-lg border border-dashed border-neutral-300 p-3">
                                                    <p className="mb-2 text-xs font-bold uppercase text-neutral-500">
                                                        Volumes fechados disponiveis
                                                    </p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {looseVolumes
                                                            .filter((v) => !v.open && v.code !== current.code)
                                                            .map((v) => (
                                                                <Btn
                                                                    key={v.code}
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    onClick={() => handleBuildScan(v.code)}
                                                                >
                                                                    bipar {v.code}
                                                                </Btn>
                                                            ))}
                                                        <SimChip />
                                                    </div>
                                                </div>
                                            )}
                                        <div className="flex items-center gap-3 pt-1">
                                            <Btn
                                                onClick={() => {
                                                    updateContainer(current.code, { open: false });
                                                    setActiveContainer(null);
                                                }}
                                                disabled={containerItemTotal(current) === 0 && !current.children?.length}
                                            >
                                                Fechar {current.code}
                                            </Btn>
                                            <span className="text-sm text-neutral-500">
                                                {containerInvIds(current).length > 1
                                                    ? 'Vai fechar como MISTO'
                                                    : 'Vai fechar como UNICO'}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </Card>
                        )}

                        <Card title="Alocacao restante">
                            <div className="space-y-2">
                                {activeItems.map((i) => (
                                    <div key={i.invId}>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-neutral-700">
                                                {i.sku}{' '}
                                                <span className="text-xs text-neutral-400">
                                                    ({effectivePolicy(i) || 'sem definicao'})
                                                </span>
                                            </span>
                                        </div>
                                        <Progress done={packedQty[i.invId] || 0} total={i.qty} />
                                    </div>
                                ))}
                            </div>
                        </Card>
                        <Question id="montagem" />
                    </div>
                );
            }

            /* ---------- 7. declaracao ---------- */
            case 'declaracao':
                return (
                    <div className="space-y-4">
                        <Banner tone="amber" icon={Upload} title="Etapa de espera: o sistema depende do Mercado Livre">
                            <p>
                                O sistema entrega os numeros, a Karine declara no ML e baixa as etiquetas. Nada aqui pode
                                ser validado automaticamente.
                            </p>
                        </Banner>
                        <Card title="Informe no Mercado Livre" subtitle="Numeros apurados na montagem">
                            <div className="grid gap-3 sm:grid-cols-4">
                                {[
                                    { label: 'Pallets', value: pallets.length },
                                    { label: 'Pallets mistos', value: pallets.filter(isMixed).length },
                                    { label: 'Pallets unicos', value: pallets.filter((p) => !isMixed(p)).length },
                                    { label: 'Volumes soltos', value: looseVolumes.length },
                                ].map((s) => (
                                    <div key={s.label} className="rounded-lg bg-neutral-50 p-3">
                                        <p className="text-xs uppercase text-neutral-500">{s.label}</p>
                                        <p className="font-mono text-3xl font-bold text-black">{s.value}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 rounded-lg bg-neutral-50 p-3 text-sm text-neutral-700">
                                Estimativa na abertura: <strong className="font-mono">{estimatedPallets.toFixed(2)}</strong>{' '}
                                pallets. Real depois da montagem:{' '}
                                <strong className="font-mono">{pallets.length}</strong> pallets e{' '}
                                <strong className="font-mono">{looseVolumes.length}</strong> volumes. Sao numeros
                                diferentes e servem para coisas diferentes.
                            </div>
                            <div className="mt-4 flex flex-wrap items-center gap-3">
                                <Btn
                                    onClick={() => {
                                        setDeclared(true);
                                        generateMlLabels();
                                    }}
                                    disabled={declared}
                                >
                                    {declared ? 'Etiquetas baixadas' : 'Declarei no ML e baixei as etiquetas'}
                                </Btn>
                                <SimChip />
                            </div>
                        </Card>
                        {mlLabels.length > 0 && (
                            <Card title="Etiquetas recebidas do Mercado Livre">
                                <div className="flex flex-wrap gap-2">
                                    {mlLabels.map((l) => (
                                        <span
                                            key={l.code}
                                            className="inline-flex items-center gap-2 rounded-lg border border-neutral-300 px-3 py-1.5 font-mono text-sm"
                                        >
                                            {l.code}
                                            <Chip tone={l.mixed ? 'amber' : 'neutral'}>{l.mixed ? 'misto' : 'unico'}</Chip>
                                        </span>
                                    ))}
                                </div>
                            </Card>
                        )}
                        <Question id="declaracao" />
                    </div>
                );

            /* ---------- 8. depara ---------- */
            case 'depara':
                return (
                    <div className="space-y-4">
                        <Banner tone="neutral" icon={ScanLine} title="Aqui a etiqueta interna prova seu valor">
                            <p>
                                Cada container interno recebe uma etiqueta do ML. O sistema bloqueia etiqueta de misto em
                                container unico e vice-versa. Tente errar de proposito.
                            </p>
                        </Banner>
                        {deparaError && (
                            <Banner tone="red" icon={AlertTriangle} title="Vinculo bloqueado">
                                <p>{deparaError}</p>
                            </Banner>
                        )}
                        {shippable.length === 0 && (
                            <Banner tone="amber" icon={AlertTriangle}>
                                <p>Nenhum container montado. Volte para a montagem.</p>
                            </Banner>
                        )}
                        {shippable.map((c) => (
                            <Card
                                key={c.code}
                                title={c.code}
                                subtitle={`${c.type} · ${containerItemTotal(c)} un. · ${containerInvIds(c).length} Inventory ID`}
                                right={
                                    <div className="flex items-center gap-2">
                                        <Chip tone={isMixed(c) ? 'amber' : 'neutral'}>{isMixed(c) ? 'misto' : 'unico'}</Chip>
                                        {c.mlLabel && (
                                            <Chip tone="green">
                                                <Check size={11} /> {c.mlLabel}
                                            </Chip>
                                        )}
                                    </div>
                                }
                            >
                                {c.mlLabel ? (
                                    <p className="text-sm text-neutral-600">
                                        Vinculado a <span className="font-mono font-bold text-black">{c.mlLabel}</span>.
                                    </p>
                                ) : (
                                    <div className="flex flex-wrap gap-2">
                                        {mlLabels
                                            .filter((l) => !l.used)
                                            .map((l) => (
                                                <Btn
                                                    key={l.code}
                                                    size="sm"
                                                    variant="secondary"
                                                    onClick={() => attachMlLabel(c.code, l.code)}
                                                >
                                                    bipar {l.code}
                                                    <Chip tone={l.mixed ? 'amber' : 'neutral'}>
                                                        {l.mixed ? 'misto' : 'unico'}
                                                    </Chip>
                                                </Btn>
                                            ))}
                                        {mlLabels.filter((l) => !l.used).length === 0 && (
                                            <p className="text-sm text-neutral-500">Sem etiqueta do ML disponivel.</p>
                                        )}
                                    </div>
                                )}
                            </Card>
                        ))}
                        <Question id="depara" />
                    </div>
                );

            /* ---------- 9. pronto ---------- */
            case 'pronto':
                return (
                    <div className="space-y-4">
                        <Card title="Transporte">
                            <div className="grid gap-4 sm:grid-cols-3">
                                <Field label="Transportadora">
                                    <input
                                        className={inputCls}
                                        value={transport.haulier}
                                        onChange={(e) => setTransport({ ...transport, haulier: e.target.value })}
                                        placeholder="Ex: Coleta Mercado Livre"
                                    />
                                </Field>
                                <Field label="Motorista">
                                    <input
                                        className={inputCls}
                                        value={transport.driver}
                                        onChange={(e) => setTransport({ ...transport, driver: e.target.value })}
                                        placeholder="Nome"
                                    />
                                </Field>
                                <Field label="Placa">
                                    <input
                                        className={inputCls}
                                        value={transport.plate}
                                        onChange={(e) => setTransport({ ...transport, plate: e.target.value })}
                                        placeholder="ABC1D23"
                                    />
                                </Field>
                            </div>
                        </Card>
                        <Card title="Fotos da carga montada" subtitle="Minimo de 4 fotos, igual ao sistema atual">
                            <div className="flex flex-wrap items-center gap-2">
                                {Array.from({ length: 4 }).map((_, idx) => (
                                    <div
                                        key={idx}
                                        className={cls(
                                            'flex h-20 w-20 items-center justify-center rounded-lg border-2 border-dashed',
                                            idx < photos ? 'border-green-500 bg-green-50' : 'border-neutral-300',
                                        )}
                                    >
                                        {idx < photos ? (
                                            <Check size={22} className="text-green-600" />
                                        ) : (
                                            <Camera size={22} className="text-neutral-400" />
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 flex items-center gap-3">
                                <Btn variant="secondary" onClick={() => setPhotos(Math.min(4, photos + 1))}>
                                    <Camera size={15} /> Adicionar foto
                                </Btn>
                                <SimChip />
                            </div>
                        </Card>
                        <Question id="pronto" />
                    </div>
                );

            /* ---------- 10. coleta ---------- */
            case 'coleta':
                return (
                    <div className="space-y-4">
                        <Card
                            title="Embarque"
                            subtitle="Bipe cada container entrando no caminhao"
                            right={<Chip tone="black">{loaded.length}/{shippable.length}</Chip>}
                        >
                            <ScanBox
                                onScan={handleCollectScan}
                                error={collectError}
                                placeholder="Etiqueta interna ou etiqueta do ML"
                                autoFocus
                            />
                            <div className="mt-4 space-y-2">
                                {shippable.map((c) => {
                                    const entry = loaded.find((l) => l.code === c.code);
                                    return (
                                        <div
                                            key={c.code}
                                            className={cls(
                                                'flex flex-wrap items-center gap-2 rounded-lg border p-3',
                                                entry ? 'border-green-300 bg-green-50' : 'border-neutral-200',
                                            )}
                                        >
                                            <span className="font-mono font-bold text-black">{c.code}</span>
                                            <Chip tone="neutral">{c.mlLabel || 'sem etiqueta ML'}</Chip>
                                            {entry ? (
                                                <span className="ml-auto flex items-center gap-1.5 font-mono text-xs text-green-700">
                                                    <Check size={13} /> embarcado {entry.at}
                                                </span>
                                            ) : (
                                                <Btn
                                                    size="sm"
                                                    variant="ghost"
                                                    className="ml-auto"
                                                    onClick={() => handleCollectScan(c.code)}
                                                >
                                                    bipar
                                                </Btn>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </Card>
                        <Card title="Video da coleta" subtitle="Minimo 1 video, igual ao sistema atual">
                            <div className="flex items-center gap-3">
                                <Btn variant="secondary" onClick={() => setVideo(true)} disabled={video}>
                                    {video ? 'Video anexado' : 'Anexar video'}
                                </Btn>
                                <SimChip />
                            </div>
                        </Card>
                        {stepDone.coleta && (
                            <Banner tone="green" icon={Check} title="Envio coletado">
                                <p>
                                    {shippable.length} containers embarcados, {pallets.length} pallets e{' '}
                                    {looseVolumes.length} volumes. Estimativa inicial era{' '}
                                    {estimatedPallets.toFixed(2)} pallets.
                                </p>
                            </Banner>
                        )}
                        <Question id="coleta" />
                    </div>
                );

            default:
                return null;
        }
    };

    /* --------------------------- layout --------------------------- */

    const StepIcon = step.icon;

    return (
        <div className="min-h-screen bg-neutral-100 p-4 font-sans text-black sm:p-6">
            <div className="mx-auto max-w-7xl">
                {/* topo */}
                <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-widest text-neutral-500">
                            Prototipo de processo · nao funcional
                        </p>
                        <h1 className="text-2xl font-bold sm:text-3xl">Outbound Full — Mercado Livre</h1>
                        <p className="mt-1 text-sm text-neutral-600">
                            Clique pelas etapas e veja se a ordem bate com a operacao real.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Btn
                            size="sm"
                            variant={showQuestions ? 'primary' : 'secondary'}
                            onClick={() => setShowQuestions(!showQuestions)}
                        >
                            <HelpCircle size={14} /> Duvidas em aberto
                        </Btn>
                        <Btn
                            size="sm"
                            variant={coletor ? 'primary' : 'secondary'}
                            onClick={() => setColetor(!coletor)}
                        >
                            {coletor ? <Smartphone size={14} /> : <Monitor size={14} />}
                            {coletor ? 'Tela do coletor' : 'Tela do PC'}
                        </Btn>
                        <Btn size="sm" variant="ghost" onClick={resetAll}>
                            <RotateCcw size={14} /> Recomecar
                        </Btn>
                    </div>
                </div>

                <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
                    {/* trilho de etapas */}
                    <aside className="space-y-1">
                        {STEPS.map((s, idx) => {
                            const Icon = s.icon;
                            const isCurrent = idx === stepIndex;
                            const done = stepDone[s.id];
                            const isSkipped = skipped[s.id];
                            return (
                                <button
                                    key={s.id}
                                    type="button"
                                    onClick={() => setStepIndex(idx)}
                                    className={cls(
                                        'flex w-full items-center gap-3 rounded-lg border p-2.5 text-left transition-colors',
                                        isCurrent
                                            ? 'border-black bg-white'
                                            : 'border-transparent bg-white/60 hover:bg-white',
                                    )}
                                >
                                    <span
                                        className={cls(
                                            'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
                                            done ? 'bg-green-600 text-white' : isCurrent ? 'bg-black text-white' : 'bg-neutral-200 text-neutral-500',
                                        )}
                                    >
                                        {done ? <Check size={15} /> : <Icon size={15} />}
                                    </span>
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate text-sm font-semibold">{s.label}</span>
                                        <span className="mt-0.5 flex items-center gap-1">
                                            <Chip tone="neutral">{s.persona}</Chip>
                                            <Chip tone={s.device === 'coletor' ? 'black' : 'neutral'}>
                                                {s.device === 'coletor' ? 'coletor' : 'pc'}
                                            </Chip>
                                            {isSkipped && <Chip tone="green">pulada</Chip>}
                                        </span>
                                    </span>
                                    <span className="font-mono text-[10px] text-neutral-400">{idx + 1}</span>
                                </button>
                            );
                        })}
                        <div className="mt-3 rounded-lg border border-neutral-200 bg-white p-3 text-xs text-neutral-600">
                            <p className="mb-1 font-bold uppercase tracking-wide text-neutral-500">Sobre os papeis</p>
                            <p>
                                Persona e dispositivo aqui sao indicativos, nao restricoes. Quem abre o outbound cai
                                sempre na etapa corrente — o Wagner pode bipar num leitor USB no PC sem quebrar o fluxo.
                            </p>
                        </div>
                    </aside>

                    {/* painel */}
                    <main className="min-w-0">
                        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-neutral-200 bg-white p-4">
                            <div className="flex items-center gap-3">
                                <span className="rounded-lg bg-black p-2.5 text-white">
                                    <StepIcon size={20} />
                                </span>
                                <div>
                                    <p className="font-mono text-[11px] uppercase tracking-wide text-neutral-500">
                                        status: {step.id}
                                    </p>
                                    <h2 className="text-lg font-bold leading-tight">{step.label}</h2>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Btn
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => setStepIndex(Math.max(0, stepIndex - 1))}
                                    disabled={stepIndex === 0}
                                >
                                    Voltar
                                </Btn>
                                <Btn
                                    size="sm"
                                    onClick={() => setStepIndex(Math.min(STEPS.length - 1, stepIndex + 1))}
                                    disabled={stepIndex === STEPS.length - 1 || !stepDone[step.id]}
                                >
                                    Avancar <ChevronRight size={14} />
                                </Btn>
                            </div>
                        </div>

                        {!stepDone[step.id] && (
                            <p className="mb-4 flex items-center gap-2 text-sm text-neutral-500">
                                <AlertTriangle size={14} /> Conclua esta etapa para liberar a proxima.
                            </p>
                        )}

                        {coletor ? (
                            <div className="flex justify-center">
                                <div className="w-full max-w-[380px] rounded-[2rem] border-8 border-neutral-800 bg-neutral-100 p-3 shadow-xl">
                                    <p className="mb-2 text-center font-mono text-[10px] uppercase text-neutral-500">
                                        coletor de dados · 380px
                                    </p>
                                    <div className="max-h-[70vh] space-y-4 overflow-y-auto">{renderStep()}</div>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">{renderStep()}</div>
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
}
